import { cva, type VariantProps } from "class-variance-authority"
import type * as React from "react"

import { cn } from "~/lib/utils"

const alertVariants = cva(
	"relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg surface-raised text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
	{
		variants: {
			density: {
				default: "px-4 py-3",
				compact: "px-2 py-1.5 [&_[data-slot=alert-description]]:text-xs"
			},
			variant: {
				default: "bg-card text-card-foreground",
				info: "border-primary/40 bg-primary/5 text-text-main",
				destructive:
					"bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 [&>svg]:text-current"
			}
		},
		defaultVariants: {
			variant: "default",
			density: "default"
		}
	}
)

function Alert({
	className,
	variant,
	density,
	...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
	return (
		<div
			data-slot="alert"
			role="alert"
			className={cn(alertVariants({ variant, density }), className)}
			{...props}
		/>
	)
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="alert-title"
			className={cn(
				"col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
				className
			)}
			{...props}
		/>
	)
}

function AlertDescription({
	className,
	...props
}: React.ComponentProps<"div">) {
	return (
		<div
			data-slot="alert-description"
			className={cn(
				"col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed",
				className
			)}
			{...props}
		/>
	)
}

export { Alert, AlertDescription, AlertTitle }
